import { Box, Divider, Group } from '@byecode/ui'
import type { FieldProtocol, IndependentAction } from '@lighthouse/core'
import { FlowSelectSourceType } from '@lighthouse/core'
import type { FlowNode, NodeTypes, UpdateRecordActionConfig } from '@lighthouse/shared'
import { getUpstreamRealDsId, ListItem4ByecodeUi, OUTPUT_RECORD_NODES, OUTPUT_SINGLE_DATA_NODES } from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { SettingRecordContent } from '@/containers/FlowDetail/components/SettingRecordContent'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import OutputVariable from '../../../OutputVariable'
import * as SC from '../../styles'
import { SelectRecordControlled } from '../component/SelectRecordControlled'

interface UpdateRecordActionConfigureProps {
    nodeType?: NodeTypes
    value: UpdateRecordActionConfig
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
    onChange: (value: UpdateRecordActionConfig) => void
}

export const UpdateRecordActionConfigure: React.FC<UpdateRecordActionConfigureProps> = ({
    nodeType,
    value,
    allParentNodes,
    allParentActions,
    actionTypeSwitcher,
    onChange
}) => {
    const { type } = useFlowContainerContext()
    const isWorkflow = !!type
    const { nodeId, selectType, dataSourceId: dsId, fields } = value

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const dataSourceId = useMemo(() => {
        if (selectType === FlowSelectSourceType.UPSTREAM) {
            return getUpstreamRealDsId(nodeId, allParentNodes || [])
        }
        return dsId
    }, [allParentNodes, dsId, nodeId, selectType])

    const upstreamNodesForVariableSelect = useMemo(
        () =>
            allParentNodes?.filter(item => {
                return !!OUTPUT_SINGLE_DATA_NODES.has(item.data.nodeType)
                // return !!(selectType === FlowSelectSourceType.UPSTREAM && item.id === nodeId)
            }),
        [allParentNodes]
    )
    const upstreamNodesForSource = useMemo(
        () => allParentNodes?.filter(item => OUTPUT_RECORD_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )

    const outputOptions = useMemo(() => {
        const current: Record<string, FieldProtocol> = dataSourceList.find(item => item.id === dataSourceId)?.schema || {}
        const values = Object.values(current)

        return values.map(item => ({
            id: item.id,
            label: item.name,
            desc: '记录的字段值',
            type: item.innerType
        }))
    }, [dataSourceId, dataSourceList])

    return (
        <SC.Container>
            {actionTypeSwitcher && (
                <>
                    <Group label="动作配置">
                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel>类型</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>{actionTypeSwitcher}</SC.FormItemContent>
                        </ListItem4ByecodeUi>
                    </Group>
                    <Divider />
                </>
            )}

            <Group label="选择记录">
                <SelectRecordControlled
                    value={value}
                    onChange={val => onChange(val as UpdateRecordActionConfig)}
                    upstreamNodesForVariableSelect={upstreamNodesForVariableSelect}
                    upstreamNodesForSource={upstreamNodesForSource}
                    allParentActions={allParentActions}
                    upstreamRecordIsFilterAndSort={false}
                    disableUpstreamRecord={!isWorkflow}
                />
            </Group>

            {dataSourceId && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <Group label="设置记录内容">
                        <Box px={8}>
                            <SettingRecordContent
                                allParentNodes={allParentNodes}
                                upstreamNodes={upstreamNodesForVariableSelect}
                                allParentActions={allParentActions}
                                dataSourceId={dataSourceId}
                                value={fields}
                                onChange={val => onChange({ ...value, fields: val })}
                            />
                        </Box>
                    </Group>
                </>
            )}
            <OutputVariable options={outputOptions} />
        </SC.Container>
    )
}
